<template>
  <div class="home">
    <div class="welcome">
      <h1>Bienvenido a IP Group</h1>
      <p v-if="isAuthenticated">Hola, {{ user.username }}!</p>
      <p v-else>Por favor, inicia sesión para acceder a todas las funcionalidades.</p>
      <div class="actions">
        <router-link v-if="!isAuthenticated" to="/login" class="btn btn-primary">Iniciar Sesión</router-link>
        <router-link v-if="!isAuthenticated" to="/register" class="btn btn-secondary">Registrarse</router-link>
        <router-link v-if="isAuthenticated" to="/profile" class="btn btn-primary">Mi Perfil</router-link>
        <button v-if="isAuthenticated" @click="logout" class="btn btn-secondary">Cerrar Sesión</button>
      </div>
    </div>
    <div class="intro">
      <h2>¿Qué es IP Group?</h2>
      <p>
        IP Group es una plataforma integral compuesta por varias aplicaciones:
      </p>
      <ul class="app-list">
        <li v-for="m in modulos" :key="m.ruta">
          <router-link :to="m.ruta"><b>{{ m.nombre }}</b></router-link>: {{ m.texto }}
        </li>
        <li v-if="!modulos.length">Tu usuario todavía no tiene acceso a ningún módulo. Pídeselo al administrador.</li>
      </ul>
      <template v-if="esAdmin">
      <h3>Comandos útiles para administración</h3>
      <ul class="help-list">
        <li><b>Ver logs del backend:</b><br>
          <code>journalctl -u argos-prod -f</code>
        </li>
        <li><b>Reiniciar backend:</b><br>
          <code>sudo systemctl restart argos-prod</code>
        </li>
        <li><b>Ver estado backend:</b><br>
          <code>systemctl status argos-prod</code>
        </li>
        <li><b>Detener backend:</b><br>
          <code>sudo systemctl stop argos-prod</code>
        </li>
        <li><b>Iniciar frontend (modo desarrollo):</b><br>
          <code>cd frontend &amp;&amp; npm run serve</code>
        </li>
        <li><b>Construir frontend (producción):</b><br>
          <code>~/apps/argos/prod/deploy.sh</code> (nunca compilar a mano en prod)
        </li>
      </ul>
      </template>
      <p style="margin-top:1em;">Si necesitas ayuda adicional, contacta al administrador del sistema.</p>
    </div>
    <div class="features">
      <h2>Características</h2>
      <div class="feature-list">
        <div class="feature">
          <h3>Aplicación Moderna</h3>
          <p>Desarrollada con FastAPI y Vue.js</p>
        </div>
        <div class="feature">
          <h3>API Robusta</h3>
          <p>Backend potente con autenticación JWT</p>
        </div>
        <div class="feature">
          <h3>UI Responsiva</h3>
          <p>Interfaz adaptable a todos los dispositivos</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'HomeView',
  setup() {
    const store = useStore();
    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated']);
    const user = computed(() => store.state.auth.user);
    const esAdmin = computed(() => store.getters['auth/esAdmin']);
    const MODULOS = [
      { categoria: 'csb', ruta: '/csb', nombre: 'CSB', texto: 'Gestión de compras, seguimiento de ítems y procesos logísticos.' },
      { categoria: 'arriendos', ruta: '/arriendos', nombre: 'Arriendos', texto: 'Administración de contratos de arriendo, pagos y propiedades.' },
      { categoria: 'empresas', ruta: '/empresas', nombre: 'Empresas', texto: 'Cuentas bancarias y documentos tributarios.' },
      { categoria: 'indicadores', ruta: '/indicadores', nombre: 'Indicadores', texto: 'Indicadores económicos y financieros.' },
      { categoria: 'pjud', ruta: '/pjud', nombre: 'PJUD', texto: 'Seguimiento de causas judiciales.' },
      { categoria: 'sangre', ruta: '/sangre', nombre: 'Exámenes de sangre', texto: 'Historial de exámenes.' },
      { categoria: 'seguros', ruta: '/seguros', nombre: 'Seguros', texto: 'Reembolsos de salud MetLife: en proceso e histórico.' },
    ];
    const modulos = computed(() => MODULOS.filter(m => store.getters['auth/puedeCategoria'](m.categoria)));
    const logout = () => {
      store.dispatch('auth/logout');
    };
    return {
      isAuthenticated,
      user,
      esAdmin,
      modulos,
      logout
    };
  }
};
</script>

<style lang="scss" scoped>
.home {
  padding: 2rem 1rem;
}

.welcome {
  text-align: center;
  margin-bottom: 3rem;
  
  h1 {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: #2d3748;
  }
  
  p {
    font-size: 1.1rem;
    color: #4a5568;
    margin-bottom: 2rem;
  }
}

.actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  
  .btn {
    padding: 0.75rem 1.5rem;
    border-radius: 4px;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.2s;
    
    &.btn-primary {
      background-color: #4299e1;
      color: white;
      border: none;
      
      &:hover {
        background-color: #3182ce;
      }
    }
    
    &.btn-secondary {
      background-color: transparent;
      color: #4299e1;
      border: 1px solid #4299e1;
      
      &:hover {
        background-color: #ebf8ff;
      }
    }
  }
}

.intro {
  max-width: 700px;
  margin: 2rem auto 2rem auto;
  background: #f8fafc;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  text-align: center;
}

.app-list {
  text-align: left;
  max-width: 600px;
  margin: 1.5rem auto 1.5rem auto;
  padding-left: 1.5rem;
  font-size: 1.08em;
}

.help-list {
  text-align: left;
  max-width: 700px;
  margin: 1.5rem auto 1.5rem auto;
  padding-left: 1.5rem;
  font-size: 1.05em;
}

.help-list code {
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.98em;
}

.features {
  max-width: 960px;
  margin: 0 auto;
  
  h2 {
    text-align: center;
    margin-bottom: 2rem;
    color: #2d3748;
  }
  
  .feature-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
    
    .feature {
      padding: 1.5rem;
      background-color: #fff;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      
      h3 {
        margin-bottom: 0.75rem;
        color: #2d3748;
      }
      
      p {
        color: #4a5568;
      }
    }
  }
}

@media (max-width: 640px) {
  .actions {
    flex-direction: column;
    
    .btn {
      width: 100%;
    }
  }
}
</style>